
import { useMemo } from 'react';
import {
    calculateSEOScore,
    checkCompliance,
    checkCharacterLimit,
    generateBackendSearchTerms,
    CharacterCheck,
    SEOScore,
} from '../../../utils/amazonOptimization';

export type CheckResult = CharacterCheck;
export type { SEOScore };

interface UseSEOAnalysisPanelProps {
    title: string;
    bulletPoints: string[];
    keywords: string[];
}

export const useSEOAnalysisPanel = ({
    title,
    bulletPoints,
    keywords,
}: UseSEOAnalysisPanelProps) => {
    // 计算SEO评分
    const seoScore = useMemo(() =>
        calculateSEOScore(title, bulletPoints, keywords),
        [title, bulletPoints, keywords]
    );

    // 合规检查
    const complianceIssues = useMemo(() => {
        const titleIssues = checkCompliance(title).map(i => ({ ...i, location: '标题' }));
        const bulletIssues = bulletPoints.flatMap((bp, idx) =>
            checkCompliance(bp).map(i => ({ ...i, location: `卖点${idx + 1}` }))
        );
        return [...titleIssues, ...bulletIssues];
    }, [title, bulletPoints]);

    // 字符数检查
    const titleCharCheck = useMemo(() => checkCharacterLimit(title, 'title'), [title]);
    const bulletCharChecks = useMemo(() =>
        bulletPoints.map(bp => checkCharacterLimit(bp, 'bullet_point')),
        [bulletPoints]
    );

    // 后台搜索词建议
    const backendSearchTerms = useMemo(() =>
        generateBackendSearchTerms(keywords, title, bulletPoints),
        [keywords, title, bulletPoints]
    );

    // 评分等级
    const getScoreLevel = (score: number) => {
        if (score >= 90) return { color: '#52c41a', text: '优秀', status: 'success' as const };
        if (score >= 75) return { color: '#1890ff', text: '良好', status: 'normal' as const };
        if (score >= 60) return { color: '#faad14', text: '一般', status: 'normal' as const };
        return { color: '#ff4d4f', text: '需改进', status: 'exception' as const };
    };

    const scoreLevel = getScoreLevel(seoScore.overall);

    return {
        seoScore,
        complianceIssues,
        titleCharCheck,
        bulletCharChecks,
        backendSearchTerms,
        scoreLevel,
    };
};
